/**
 * Progress Upload Component
 *
 * Interface for uploading progress photos with GPS and metadata.
 * Each photo can have its own individual caption/description.
 *
 * @module pages/progress/ProgressUpload
 */

import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  ProgressBar,
  Badge,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import { uploadProgressPhotos } from '../../services/progressService';
import { listProjects } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import { FILE_UPLOAD, PROJECT_STATUS } from '../../utils/constants';
import DashboardLayout from '../../components/layout/DashboardLayout';

const ProgressUpload = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin, isTechnician, isSupervisor } = useAuth();

  const [selectedProject, setSelectedProject] = useState(searchParams.get('projectId') || '');
  const [files, setFiles] = useState([]);
  const [includeGPS, setIncludeGPS] = useState(true);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [error, setError] = useState(null);

  // Al supervisor le salía "Acceso Denegado" mientras la base lo autorizaba:
  // `supervisor_upload_progress_photos (INSERT)` existe en producción, y la
  // matriz dice "Subir foto de progreso: Supervisor ✅". Le estábamos negando
  // en la pantalla algo que ya tenía concedido en la base.
  const canUpload = isAdmin || isTechnician || isSupervisor;

  // Fetch projects
  const { data: projectsData } = useQuery({
    queryKey: ['projects-active'],
    // Issue #296: 'active' no longer exists in the live status set; the
    // in-execution status is now 'in_progress'.
    queryFn: () => listProjects({ status: PROJECT_STATUS.IN_PROGRESS, pageSize: 100 }),
  });

  const projects = projectsData?.data || [];

  // Get GPS location
  const getGPSLocation = useCallback(() => {
    if (navigator.geolocation && includeGPS) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          toast.success('Ubicación GPS obtenida');
        },
        (error) => {
          console.error('GPS error:', error);
          toast.warning('No se pudo obtener la ubicación GPS');
        }
      );
    }
  }, [includeGPS]);

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    setError(null);

    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map((file) => {
        if (file.file.size > FILE_UPLOAD.MAX_SIZE) {
          return `${file.file.name}: El archivo es muy grande (máx 10MB)`;
        }
        return `${file.file.name}: Tipo de archivo no permitido`;
      });
      setError(errors.join(', '));
      return;
    }

    // Compress and add files with individual captions
    const compressedFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        try {
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          const compressed = await imageCompression(file, options);
          return {
            file: compressed,
            preview: URL.createObjectURL(compressed),
            name: file.name,
            size: compressed.size,
            caption: '', // Individual caption per file
          };
        } catch (error) {
          console.error('Compression error:', error);
          return {
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            size: file.size,
            caption: '',
          };
        }
      })
    );

    setFiles((prev) => [...prev, ...compressedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: FILE_UPLOAD.MAX_SIZE,
  });

  // Update individual file caption
  const updateFileCaption = (index, caption) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      newFiles[index] = { ...newFiles[index], caption };
      return newFiles;
    });
  };

  // Remove file
  const removeFile = (index) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  // Handle upload - upload each photo with its individual caption
  const handleUpload = async () => {
    if (!selectedProject) {
      toast.error('Debes seleccionar un proyecto');
      return;
    }

    if (files.length === 0) {
      toast.error('Debes seleccionar al menos una foto');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setCurrentUploadIndex(0);
      setError(null);

      let successCount = 0;
      const uploadErrors = [];

      // Upload each file with its individual caption
      for (let i = 0; i < files.length; i++) {
        const fileData = files[i];
        setCurrentUploadIndex(i + 1);
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));

        const result = await uploadProgressPhotos(
          selectedProject,
          [fileData.file],
          fileData.caption || null, // Individual caption
          includeGPS && gpsCoords ? gpsCoords : null
        );

        if (result.error) {
          uploadErrors.push({
            fileName: fileData.name,
            error: result.error.message,
          });
        } else if (result.errors?.length > 0) {
          uploadErrors.push(...result.errors);
        } else {
          successCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} foto(s) subida(s) exitosamente`);
      }

      if (uploadErrors.length > 0) {
        const errorDetails = uploadErrors.map(e =>
          `${e.fileName}: ${e.error}`
        ).join(', ');
        console.error('Upload errors:', errorDetails);
        toast.error(`${uploadErrors.length} foto(s) fallaron`);
        setError(errorDetails);
      }

      // Clean up and navigate only if at least one photo was uploaded successfully
      if (successCount > 0) {
        files.forEach((file) => URL.revokeObjectURL(file.preview));
        // `/progreso/galeria/:id` is not in `technicianAllowedPaths`
        // (PrivateLayout.js), so sending a technician there means PrivateLayout
        // immediately bounces them to /tecnico/dashboard: they see the green
        // toast and never see the photos they just uploaded. Land them on the
        // photos tab of their own portal instead.
        navigate(
          isTechnician
            ? `/tecnico/proyectos/${selectedProject}?tab=photos`
            : `/progreso/galeria/${selectedProject}`
        );
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Error al subir las fotos');
      toast.error('Error al subir las fotos');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setCurrentUploadIndex(0);
    }
  };

  if (!canUpload) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="warning">
            <Alert.Heading>Acceso Denegado</Alert.Heading>
            <p>No tienes permisos para subir fotos de progreso.</p>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="h3 mb-0">Subir Fotos de Progreso</h1>
          <p className="text-muted">Documenta el avance de los proyectos</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          {/* Project Selector */}
          <Card className="mb-3">
            <Card.Header>
              <i className="bi bi-folder me-2"></i>
              Proyecto
            </Card.Header>
            <Card.Body>
              <Form.Select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                disabled={uploading}
              >
                <option value="">Seleccionar proyecto...</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.project_number} - {project.title}
                  </option>
                ))}
              </Form.Select>
            </Card.Body>
          </Card>

          {/* File Upload Zone */}
          <Card className="mb-3">
            <Card.Header>
              <i className="bi bi-cloud-upload me-2"></i>
              Seleccionar Fotos
            </Card.Header>
            <Card.Body>
              <div
                {...getRootProps()}
                className={`dropzone p-5 border-2 border-dashed rounded text-center ${
                  isDragActive ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'
                }`}
                style={{ cursor: 'pointer' }}
              >
                <input {...getInputProps()} />
                <i className="bi bi-cloud-arrow-up fs-1 text-primary mb-3 d-block"></i>
                {isDragActive ? (
                  <p className="mb-0">Suelta las fotos aquí...</p>
                ) : (
                  <>
                    <p className="mb-2">
                      Arrastra y suelta fotos aquí, o haz clic para seleccionar
                    </p>
                    <small className="text-muted">
                      JPG, PNG - Máximo 10MB por archivo
                    </small>
                  </>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Photos with Individual Captions */}
          {files.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <i className="bi bi-images me-2"></i>
                Fotos Seleccionadas ({files.length})
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  {files.map((file, index) => (
                    <Col key={index} xs={12} md={6}>
                      <Card className="h-100">
                        <div className="position-relative">
                          <Card.Img
                            variant="top"
                            src={file.preview}
                            alt={file.name}
                            style={{ height: '180px', objectFit: 'cover' }}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0 m-2"
                            onClick={() => removeFile(index)}
                            disabled={uploading}
                          >
                            <i className="bi bi-x-lg"></i>
                          </Button>
                          <Badge
                            bg="dark"
                            className="position-absolute bottom-0 start-0 m-2"
                          >
                            {(file.size / 1024).toFixed(0)} KB
                          </Badge>
                        </div>
                        <Card.Body className="p-2">
                          <small className="d-block text-truncate mb-2 text-muted">
                            {file.name}
                          </small>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Descripción de esta foto..."
                            value={file.caption}
                            onChange={(e) => updateFileCaption(index, e.target.value)}
                            disabled={uploading}
                            className="small"
                            maxLength={300}
                          />
                          <Form.Text className="text-muted">
                            {file.caption.length}/300
                          </Form.Text>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={4}>
          {/* GPS Options */}
          <Card className="mb-3">
            <Card.Header>
              <i className="bi bi-geo-alt me-2"></i>
              Ubicación GPS
            </Card.Header>
            <Card.Body>
              <Form.Check
                type="checkbox"
                label="Incluir ubicación GPS"
                checked={includeGPS}
                onChange={(e) => setIncludeGPS(e.target.checked)}
                disabled={uploading}
              />
              {includeGPS && (
                <div className="mt-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100"
                    onClick={getGPSLocation}
                    disabled={uploading}
                  >
                    <i className="bi bi-crosshair me-2"></i>
                    Obtener Ubicación Actual
                  </Button>
                  {gpsCoords && (
                    <Alert variant="success" className="mt-2 mb-0 small">
                      <i className="bi bi-check-circle me-1"></i>
                      GPS: {gpsCoords.latitude.toFixed(6)}, {gpsCoords.longitude.toFixed(6)}
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Upload Summary */}
          <Card className="mb-3">
            <Card.Header>
              <i className="bi bi-info-circle me-2"></i>
              Resumen
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span>Proyecto:</span>
                <strong>
                  {selectedProject ? projects.find((p) => p.id === selectedProject)?.project_number : '-'}
                </strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Fotos seleccionadas:</span>
                <strong>{files.length}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Con descripción:</span>
                <strong>{files.filter(f => f.caption.trim()).length}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>GPS incluido:</span>
                <strong>{includeGPS && gpsCoords ? 'Sí' : 'No'}</strong>
              </div>

              {uploading && (
                <div className="mt-3">
                  <small className="d-block mb-1">
                    Subiendo foto {currentUploadIndex} de {files.length}...
                  </small>
                  <ProgressBar
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    animated
                    striped
                  />
                </div>
              )}

              <div className="d-grid gap-2 mt-3">
                <Button
                  variant="primary"
                  onClick={handleUpload}
                  disabled={!selectedProject || files.length === 0 || uploading}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cloud-upload me-2"></i>
                      Subir {files.length} Foto{files.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate(-1)}
                  disabled={uploading}
                >
                  Cancelar
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      </Container>
    </DashboardLayout>
  );
};

export default ProgressUpload;
